import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { BsChatDotsFill } from "react-icons/bs";
import { IoAttach, IoCheckmarkDone, IoClose, IoDocumentTextOutline, IoSend } from "react-icons/io5";
import { isAuthenticated } from "../../services/auth.api";
import { uploadChatFiles } from "../../services/chat.api";
import useSupportChat from "./useSupportChat";
import "./chatWidget.css";

const CHANNEL = "customer";
const MAX_FILES = 5;
const ACCEPT = "image/jpeg,image/png,image/webp,image/gif,image/heic,application/pdf";

const timeOf = (iso) =>
  new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

const Attachments = ({ files }) =>
  files?.length ? (
    <div className="ofc-attachments">
      {files.map((file) =>
        file.mime?.startsWith("image/") ? (
          <a key={file.url} href={file.url} target="_blank" rel="noreferrer">
            <img src={file.url} alt={file.name} />
          </a>
        ) : (
          <a key={file.url} href={file.url} target="_blank" rel="noreferrer" className="ofc-file">
            <IoDocumentTextOutline /> {file.name}
          </a>
        ),
      )}
    </div>
  ) : null;

const Message = ({ message, seen }) => {
  if (message.from === "system") {
    return <div className="ofc-system">{message.body}</div>;
  }
  const mine = message.from === "you";
  return (
    <div className={`ofc-row ${mine ? "ofc-row--mine" : ""}`}>
      <div className={`ofc-bubble ${mine ? "ofc-bubble--mine" : "ofc-bubble--support"}`}>
        {!mine && <span className="ofc-sender">OwnFood Support</span>}
        {message.body && <span className="ofc-text">{message.body}</span>}
        <Attachments files={message.attachments} />
      </div>
      <span className="ofc-meta">
        {timeOf(message.createdAt)}
        {mine && seen && (
          <span className="ofc-seen">
            <IoCheckmarkDone /> Seen
          </span>
        )}
      </span>
    </div>
  );
};

/**
 * "Chat with us" — a floating button on every page that opens a chat with
 * OwnFood Support. Logged-out visitors are asked to log in first.
 */
const ChatWidget = () => {
  const location = useLocation();
  const loggedIn = isAuthenticated();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const listRef = useRef(null);
  const fileRef = useRef(null);

  const chat = useSupportChat(CHANNEL, { enabled: loggedIn, open });
  const lastId = chat.messages[chat.messages.length - 1]?.id;

  useEffect(() => {
    if (open && listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [open, lastId, chat.supportTyping]);

  // No chat button on the login/register screens or while printing an invoice.
  if (/^\/(authentication|print)/i.test(location.pathname)) return null;

  const seenUntil = chat.conversation?.supportLastReadAt
    ? new Date(chat.conversation.supportLastReadAt).getTime()
    : 0;
  const canSend = (draft.trim() || files.length > 0) && !sending && !uploading;

  const pickFiles = async (list) => {
    const picked = Array.from(list).slice(0, MAX_FILES - files.length);
    if (!picked.length) return;
    setUploading(true);
    try {
      const uploaded = await uploadChatFiles(picked);
      setFiles((prev) => [...prev, ...uploaded].slice(0, MAX_FILES));
    } catch (err) {
      toast.error(err.message || "Couldn't upload the file");
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    if (!canSend) return;
    setSending(true);
    try {
      await chat.send({ body: draft.trim(), attachments: files });
      setDraft("");
      setFiles([]);
    } catch (err) {
      toast.error(err.message || "Message not sent — please try again");
    } finally {
      setSending(false);
    }
  };

  const status = !chat.conversation
    ? "Ask us anything — we're happy to help."
    : !chat.conversation.open
      ? "This chat is resolved. Send a message to start again."
      : chat.conversation.agentJoined
        ? "You're chatting with OwnFood Support."
        : "Thanks! We'll reply as soon as we can.";

  return (
    <>
      {open && (
        <div className="ofc-panel" role="dialog" aria-label="Chat with OwnFood Support">
          <div className="ofc-header">
            <div>
              <strong>OwnFood Support</strong>
              <span>{loggedIn ? status : "We're here to help"}</span>
            </div>
            <button type="button" className="ofc-icon-btn" onClick={() => setOpen(false)} aria-label="Close chat">
              <IoClose />
            </button>
          </div>

          {!loggedIn ? (
            <div className="ofc-empty">
              <p>Log in to chat with OwnFood Support about your orders, payments or account.</p>
              <Link to="/authentication/login" className="btn btn-primary btn-sm" onClick={() => setOpen(false)}>
                Log in
              </Link>
            </div>
          ) : (
            <>
              <div className="ofc-messages" ref={listRef}>
                {chat.hasMore && (
                  <button type="button" className="ofc-older" onClick={chat.loadOlder} disabled={chat.loadingOlder}>
                    {chat.loadingOlder ? "Loading…" : "Show earlier messages"}
                  </button>
                )}
                {chat.loading && chat.messages.length === 0 && <div className="ofc-system">Loading…</div>}
                {chat.error && <div className="ofc-system ofc-error">{chat.error}</div>}
                {!chat.loading && !chat.error && chat.messages.length === 0 && (
                  <div className="ofc-empty ofc-empty--inline">
                    <p>Hi! How can we help you today?</p>
                  </div>
                )}
                {chat.messages.map((message) => (
                  <Message
                    key={message.id}
                    message={message}
                    seen={new Date(message.createdAt).getTime() <= seenUntil}
                  />
                ))}
                {chat.supportTyping && <div className="ofc-typing">Support is typing…</div>}
              </div>

              {files.length > 0 && (
                <div className="ofc-pending">
                  {files.map((file, i) => (
                    <span key={file.url} className="ofc-chip">
                      {file.name}
                      <button type="button" onClick={() => setFiles(files.filter((_, j) => j !== i))} aria-label="Remove file">
                        <IoClose />
                      </button>
                    </span>
                  ))}
                </div>
              )}

              <div className="ofc-composer">
                <button
                  type="button"
                  className="ofc-icon-btn"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading || files.length >= MAX_FILES}
                  aria-label="Attach a file"
                >
                  <IoAttach />
                </button>
                <input
                  ref={fileRef}
                  type="file"
                  accept={ACCEPT}
                  multiple
                  hidden
                  onChange={(e) => {
                    pickFiles(e.target.files);
                    e.target.value = "";
                  }}
                />
                <textarea
                  rows={1}
                  maxLength={2000}
                  value={draft}
                  placeholder={uploading ? "Uploading…" : "Type a message…"}
                  onChange={(e) => {
                    setDraft(e.target.value);
                    if (e.target.value) chat.notifyTyping();
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      submit();
                    }
                  }}
                />
                <button type="button" className="ofc-send" onClick={submit} disabled={!canSend} aria-label="Send">
                  <IoSend />
                </button>
              </div>
            </>
          )}
        </div>
      )}

      <button
        type="button"
        className="ofc-launcher"
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? "Close chat" : "Chat with OwnFood Support"}
      >
        {open ? <IoClose /> : <BsChatDotsFill />}
        {!open && chat.unread > 0 && <span className="ofc-badge">{chat.unread > 9 ? "9+" : chat.unread}</span>}
      </button>
    </>
  );
};

export default ChatWidget;
